import { useCallback, useState } from 'react';
import { useTranslations } from 'use-intl';
import { ApiFailure } from '@/lib/api';

/**
 * Shared submit plumbing for the auth forms: one busy flag, a top-level message,
 * and the server's field map turned into per-input messages.
 *
 * Server codes are translated, never shown raw — a coach should not read
 * `validation_failed` on screen.
 */
export function useAuthForm() {
  const t = useTranslations();
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});

  const fieldError = useCallback(
    (name: string): string | undefined => {
      const code = fields[name];
      if (!code) return undefined;
      const key = `field.${code}`;
      const translated = t(key);
      return translated === key ? t('field.custom') : translated;
    },
    [fields, t],
  );

  const submit = useCallback(
    async (action: () => Promise<void>) => {
      setBusy(true);
      setFormError(null);
      setFields({});
      try {
        await action();
        return true;
      } catch (err) {
        if (err instanceof ApiFailure) {
          setFields(err.fields ?? {});
          const key = `error.${err.code}`;
          const translated = t(key);
          // A field-level failure is already shown on the inputs; repeating a
          // vague banner above them adds noise.
          const onlyFields = err.code === 'validation_failed' && err.fields;
          setFormError(onlyFields ? null : translated === key ? t('error.generic') : translated);
        } else {
          setFormError(navigator.onLine ? t('error.generic') : t('error.offline'));
        }
        return false;
      } finally {
        setBusy(false);
      }
    },
    [t],
  );

  return { busy, formError, fieldError, submit, setFormError };
}
